import React, { useState, useEffect } from 'react';
import styles from './DashboardStatGraphs.module.css';

// API 응답 타입 정의
interface ApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: T;
}

interface CourseRatingData {
  lecture_id: number;
  lecture_title: string;
  avg_rating: number;
  rank: number;
}

const DashboardStatGraphs: React.FC = () => {
  // 강의 평점 상태
  const [ratingData, setRatingData] = useState<CourseRatingData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 드롭다운 상태
  const [selectedFilter, setSelectedFilter] = useState('전체 강의');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // 드롭다운 토글
  const toggleDropdown = () => {
    console.log(`🔽 드롭다운 토글: ${!isDropdownOpen ? '열기' : '닫기'}`);
    setIsDropdownOpen(!isDropdownOpen);
  };

  // 필터 선택
  const handleFilterSelect = (option: string) => {
    console.log(`📋 필터 선택됨: ${option}`);
    setSelectedFilter(option);
    setIsDropdownOpen(false);
  };

  // 강의별 평점 API 호출
  const fetchCourseRatings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 강의 평점 API 호출 시작...');
      
      const response = await fetch('https://campus-dev.duckdns.org/api/admin/average-rating/course', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API 요청 실패: ${response.status} ${response.statusText}`);
      }

      const result: ApiResponse<CourseRatingData[]> = await response.json();
      console.log('✅ 강의 평점 API 응답:', result);
      
      setRatingData(result.data);
      console.log('📊 강의 평점 데이터 설정 완료:', result.data);
      
    } catch (err) {
      console.error('❌ 강의 평점 API 실패:', err);
      setError('강의 평점 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 API 데이터 로딩
  useEffect(() => {
    console.log('🎬 DashboardStatGraphs useEffect 실행');
    fetchCourseRatings();
  }, []);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div className={styles.statgraph}>
      {/* 강의 평점 카드 (왼쪽) */}
      <div className={styles.card1}>
        <div className={styles.heading1}>
          <div className={styles.title}>강의 평점</div>
          <div className={styles.subtitle}>상위 5개 강의</div>
        </div>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7d7d7d' }}>
            ⭐ 강의 평점 데이터 로딩 중...
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#ff4444' }}>
            <div>❌ {error}</div>
            <button 
              onClick={fetchCourseRatings}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                background: '#448181',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔄 다시 시도
            </button>
          </div>
        ) : (
          <div className={styles.graph1}>
            {ratingData.map((course, index) => (
              <div key={course.lecture_id} className={styles.list}>
                <div className={styles.courseName}>{course.lecture_title}</div>
                <div className={styles.rating}>
                  <div className={styles.starIcon}>
                    <svg width="16" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.241 0.938748C8.88009 0.755118 8.45313 0.755118 8.09221 0.938748C7.80257 1.08611 7.63627 1.35953 7.52537 1.5712C7.40828 1.79468 7.28385 2.09386 7.1385 2.44337L6.33496 4.3753C6.28676 4.4912 6.26068 4.55321 6.23885 4.59773C6.23227 4.61115 6.22773 4.61944 6.22512 4.62398C6.2211 4.62773 6.21664 4.63098 6.21183 4.63363C6.2067 4.63472 6.19741 4.63647 6.18262 4.63858C6.13353 4.64559 6.0665 4.65123 5.94138 4.66126L3.85573 4.82847C3.4784 4.8587 3.1554 4.88459 2.90667 4.92689C2.67109 4.96695 2.35967 5.04063 2.13001 5.27055C1.84384 5.55706 1.7119 5.96312 1.77502 6.36312C1.82567 6.68412 2.03431 6.92678 2.20134 7.09766C2.37771 7.27809 2.6238 7.48887 2.91129 7.73512L4.50034 9.09632C4.59567 9.17798 4.64659 9.22194 4.68218 9.25647C4.69291 9.26687 4.69939 9.27375 4.7029 9.27764C4.70523 9.28262 4.70694 9.28787 4.70798 9.29326C4.70743 9.29847 4.70623 9.30785 4.70366 9.32257C4.69516 9.37142 4.6798 9.43691 4.65068 9.55901L4.1652 11.5942C4.07736 11.9625 4.00216 12.2776 3.96553 12.5273C3.93084 12.7637 3.90467 13.0827 4.05237 13.3721C4.23643 13.7328 4.58184 13.9838 4.98176 14.0474C5.30271 14.0984 5.59796 13.975 5.81209 13.8689C6.03819 13.7569 6.31471 13.588 6.63775 13.3907L8.42336 12.3C8.53048 12.2346 8.58802 12.1998 8.63186 12.1766C8.64506 12.1696 8.65361 12.1655 8.65839 12.1634C8.66385 12.1627 8.66937 12.1627 8.67483 12.1634C8.67961 12.1655 8.68815 12.1696 8.70136 12.1766C8.74519 12.1998 8.80273 12.2346 8.90986 12.3L10.6955 13.3907C11.0185 13.588 11.295 13.7569 11.5211 13.8689C11.7353 13.9749 12.0305 14.0984 12.3515 14.0474C12.7514 13.9838 13.0968 13.7328 13.2808 13.3721C13.4285 13.0827 13.4024 12.7637 13.3677 12.5273C13.3311 12.2777 13.2559 11.9625 13.168 11.5943L12.6825 9.55901C12.6534 9.43691 12.6381 9.37142 12.6296 9.32257C12.627 9.30785 12.6258 9.29847 12.6252 9.29326C12.6263 9.28787 12.628 9.28262 12.6303 9.27764C12.6338 9.27375 12.6403 9.26687 12.651 9.25647C12.6866 9.22195 12.7375 9.17798 12.8329 9.09632L14.4219 7.73513C14.7094 7.48888 14.9555 7.27809 15.1319 7.09766C15.2989 6.92678 15.5075 6.68412 15.5582 6.36312C15.6213 5.96312 15.4894 5.55706 15.2032 5.27055C14.9735 5.04063 14.6621 4.96695 14.4265 4.92689C14.1778 4.88459 13.8548 4.8587 13.4775 4.82846L11.3918 4.66126C11.2667 4.65123 11.1997 4.64559 11.1506 4.63858C11.1358 4.63647 11.1265 4.63472 11.1214 4.63364C11.1166 4.63098 11.1121 4.62773 11.1081 4.62398C11.1055 4.61944 11.1009 4.61115 11.0944 4.59773C11.0725 4.55321 11.0465 4.4912 10.9982 4.3753L10.1947 2.4434C10.0494 2.09388 9.92494 1.79469 9.80785 1.5712C9.69695 1.35953 9.53064 1.08611 9.241 0.938748Z" fill="#296768"/>
                    </svg>
                  </div>
                  <div className={styles.ratingValue}>{course.avg_rating.toFixed(2)}</div>
                </div>
              </div>
            ))}
            
            {/* 5개 미만일 경우 빈 항목으로 채우기 */}
            {Array.from({ length: Math.max(0, 5 - ratingData.length) }).map((_, index) => (
              <div key={`empty-${index}`} className={styles.list}>
                <div className={styles.courseName}>-</div>
                <div className={styles.rating}>
                  <div className={styles.starIcon}>
                    <svg width="16" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.3 }}>
                      <path d="M9.241 0.938748C8.88009 0.755118 8.45313 0.755118 8.09221 0.938748C7.80257 1.08611 7.63627 1.35953 7.52537 1.5712C7.40828 1.79468 7.28385 2.09386 7.1385 2.44337L6.33496 4.3753C6.28676 4.4912 6.26068 4.55321 6.23885 4.59773C6.23227 4.61115 6.22773 4.61944 6.22512 4.62398C6.2211 4.62773 6.21664 4.63098 6.21183 4.63363C6.2067 4.63472 6.19741 4.63647 6.18262 4.63858C6.13353 4.64559 6.0665 4.65123 5.94138 4.66126L3.85573 4.82847C3.4784 4.8587 3.1554 4.88459 2.90667 4.92689C2.67109 4.96695 2.35967 5.04063 2.13001 5.27055C1.84384 5.55706 1.7119 5.96312 1.77502 6.36312C1.82567 6.68412 2.03431 6.92678 2.20134 7.09766C2.37771 7.27809 2.6238 7.48887 2.91129 7.73512L4.50034 9.09632C4.59567 9.17798 4.64659 9.22194 4.68218 9.25647C4.69291 9.26687 4.69939 9.27375 4.7029 9.27764C4.70523 9.28262 4.70694 9.28787 4.70798 9.29326C4.70743 9.29847 4.70623 9.30785 4.70366 9.32257C4.69516 9.37142 4.6798 9.43691 4.65068 9.55901L4.1652 11.5942C4.07736 11.9625 4.00216 12.2776 3.96553 12.5273C3.93084 12.7637 3.90467 13.0827 4.05237 13.3721C4.23643 13.7328 4.58184 13.9838 4.98176 14.0474C5.30271 14.0984 5.59796 13.975 5.81209 13.8689C6.03819 13.7569 6.31471 13.588 6.63775 13.3907L8.42336 12.3C8.53048 12.2346 8.58802 12.1998 8.63186 12.1766C8.64506 12.1696 8.65361 12.1655 8.65839 12.1634C8.66385 12.1627 8.66937 12.1627 8.67483 12.1634C8.67961 12.1655 8.68815 12.1696 8.70136 12.1766C8.74519 12.1998 8.80273 12.2346 8.90986 12.3L10.6955 13.3907C11.0185 13.588 11.295 13.7569 11.5211 13.8689C11.7353 13.9749 12.0305 14.0984 12.3515 14.0474C12.7514 13.9838 13.0968 13.7328 13.2808 13.3721C13.4285 13.0827 13.4024 12.7637 13.3677 12.5273C13.3311 12.2777 13.2559 11.9625 13.168 11.5943L12.6825 9.55901C12.6534 9.43691 12.6381 9.37142 12.6296 9.32257C12.627 9.30785 12.6258 9.29847 12.6252 9.29326C12.6263 9.28787 12.628 9.28262 12.6303 9.27764C12.6338 9.27375 12.6403 9.26687 12.651 9.25647C12.6866 9.22195 12.7375 9.17798 12.8329 9.09632L14.4219 7.73513C14.7094 7.48888 14.9555 7.27809 15.1319 7.09766C15.2989 6.92678 15.5075 6.68412 15.5582 6.36312C15.6213 5.96312 15.4894 5.55706 15.2032 5.27055C14.9735 5.04063 14.6621 4.96695 14.4265 4.92689C14.1778 4.88459 13.8548 4.8587 13.4775 4.82846L11.3918 4.66126C11.2667 4.65123 11.1997 4.64559 11.1506 4.63858C11.1358 4.63647 11.1265 4.63472 11.1214 4.63364C11.1166 4.63098 11.1121 4.62773 11.1081 4.62398C11.1055 4.61944 11.1009 4.61115 11.0944 4.59773C11.0725 4.55321 11.0465 4.4912 10.9982 4.3753L10.1947 2.4434C10.0494 2.09388 9.92494 1.79469 9.80785 1.5712C9.69695 1.35953 9.53064 1.08611 9.241 0.938748Z" fill="#296768"/>
                    </svg>
                  </div>
                  <div className={styles.ratingValue} style={{ opacity: 0.3 }}>0.00</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 강의 시청 지속 시간 카드 (오른쪽) */}
      <div className={styles.card}>
        <div className={styles.heading}>
          <div className={styles.leading}>
            <div className={styles.title}>강의 시청 지속 시간</div>
            <div className={styles.refresh}>
              <div className={styles.timestamp}>2025.08.14. 04:00</div>
              <div className={styles.refreshIcon}>
                <svg width="16" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.40039 7.99866C2.40039 4.53768 5.20606 1.73199 8.66703 1.73199C9.87847 1.73199 11.0101 2.07614 11.9688 2.67149L12.6017 2.03856C12.7733 1.86696 13.0314 1.81563 13.2556 1.90849C13.4798 2.00136 13.626 2.22015 13.626 2.46282V4.58414C13.626 4.74327 13.5628 4.89588 13.4503 5.00841C13.3377 5.12093 13.1851 5.18414 13.026 5.18414H10.9047C10.662 5.18414 10.4432 5.03796 10.3504 4.81375C10.2575 4.58955 10.3088 4.33148 10.4804 4.15988L11.0917 3.54859C10.3715 3.15529 9.54559 2.93199 8.66703 2.93199C5.8688 2.93199 3.60039 5.20042 3.60039 7.99866C3.60039 8.33833 3.63373 8.66962 3.69713 8.98962C3.76154 9.31467 3.55024 9.63039 3.22519 9.69479C2.90014 9.7592 2.58443 9.54791 2.52002 9.22285C2.44148 8.82646 2.40039 8.41706 2.40039 7.99866Z" fill="#37383C" fillOpacity="0.61"/>
                  <path d="M14.109 6.30475C14.434 6.24036 14.7497 6.45167 14.8141 6.77672C14.8926 7.17302 14.9337 7.58231 14.9337 8.0006C14.9337 11.4616 12.128 14.2673 8.66705 14.2673C7.45596 14.2673 6.32465 13.9233 5.36612 13.3283L4.73069 13.9637C4.5591 14.1353 4.30103 14.1866 4.07682 14.0938C3.85262 14.0009 3.70643 13.7821 3.70643 13.5394L3.70643 11.4181C3.70643 11.259 3.76965 11.1064 3.88217 10.9939C3.99469 10.8813 4.1473 10.8181 4.30643 10.8181H6.42774C6.67042 10.8181 6.8892 10.9643 6.98207 11.1885C7.07494 11.4127 7.0236 11.6708 6.85201 11.8424L6.24325 12.4511C6.96321 12.8441 7.78885 13.0673 8.66705 13.0673C11.4653 13.0673 13.7337 10.7988 13.7337 8.0006C13.7337 7.66102 13.7004 7.32982 13.637 7.0099C13.5726 6.68484 13.7839 6.36914 14.109 6.30475Z" fill="#37383C" fillOpacity="0.61"/>
                </svg>
              </div>
            </div>
          </div>
          <div className={styles.categoryfilter} style={{ overflow: 'visible' }}>
            <div 
              className={styles.chip} 
              style={{ 
                position: 'relative',
                overflow: 'visible',
                zIndex: 9999999
              }}
              data-dropdown
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleDropdown();
              }}
            >
              <div className={styles.chipBackground} />
              <div 
                className={styles.chipContent}
                style={{ cursor: 'pointer' }}
              >
                <div className={styles.chipText}>{selectedFilter}</div>
                <div 
                  className={styles.chipDropdown}
                  style={{
                    transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 3.5L5 6.5L8 3.5" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              
              {/* 드롭다운 메뉴 */}
              {isDropdownOpen && (
                <div 
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    right: '0',
                    minWidth: '140px',
                    backgroundColor: 'white',
                    border: '1px solid #eaebec',
                    borderRadius: '10px',
                    zIndex: 99999999,
                    overflow: 'visible'
                  }}
                >
                  {/* 전체 강의 */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFilterSelect('전체 강의');
                    }}
                    style={{
                      padding: '6px 16px',
                      cursor: 'pointer',
                      backgroundColor: selectedFilter === '전체 강의' ? 'rgba(68, 129, 129, 0.08)' : 'transparent',
                      borderBottom: '1px solid rgba(234, 235, 236, 0.5)',
                      fontSize: '16px',
                      color: selectedFilter === '전체 강의' ? '#448181' : '#171719',
                      fontWeight: selectedFilter === '전체 강의' ? 500 : 400,
                      transition: 'all 0.2s ease',
                      fontFamily: 'Pretendard JP, sans-serif'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedFilter !== '전체 강의') {
                        e.currentTarget.style.backgroundColor = 'rgba(112, 115, 124, 0.04)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = selectedFilter === '전체 강의' ? 'rgba(68, 129, 129, 0.08)' : 'transparent';
                    }}
                  >
                    전체 강의
                  </div>
                  
                  {/* 선택 강의 */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFilterSelect('선택 강의');
                    }}
                    style={{
                      padding: '6px 16px',
                      cursor: 'pointer',
                      backgroundColor: selectedFilter === '선택 강의' ? 'rgba(68, 129, 129, 0.08)' : 'transparent',
                      fontSize: '16px',
                      color: selectedFilter === '선택 강의' ? '#448181' : '#171719',
                      fontWeight: selectedFilter === '선택 강의' ? 500 : 400,
                      transition: 'all 0.2s ease',
                      fontFamily: 'Pretendard JP, sans-serif'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedFilter !== '선택 강의') {
                        e.currentTarget.style.backgroundColor = 'rgba(112, 115, 124, 0.04)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = selectedFilter === '선택 강의' ? 'rgba(68, 129, 129, 0.08)' : 'transparent';
                    }}
                  >
                    선택 강의
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className={styles.graph}>
          {/* Y축 라벨들 */}
          <div className={styles.yAxisItem}>
            <div className={styles.yAxisLabel}>100%</div>
            <div className={styles.dividerdivider} />
          </div>
          <div className={styles.yAxisItem}>
            <div className={styles.yAxisLabel}>75%</div>
            <div className={styles.dividerdivider} />
          </div>
          <div className={styles.yAxisItem}>
            <div className={styles.yAxisLabel}>50%</div>
            <div className={styles.dividerdivider} />
          </div>
          <div className={styles.yAxisItem}>
            <div className={styles.yAxisLabel}>25%</div>
            <div className={styles.dividerdivider} />
          </div>
          <div className={styles.yAxisItem}>
            <div className={styles.yAxisLabel}>0%</div>
            <div className={styles.dividerdivider} />
          </div>
          
          {/* 그래프 영역 */}
          <div className={styles.graphPlaceholder}>
            <svg className={styles.chartSvg} viewBox="0 0 629 192">
              {/* 그라데이션 정의 */}
              <defs>
                <linearGradient id="durationGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C5E0E0" stopOpacity="0.8" />
                  <stop offset="50%" stopColor="#C5E0E0" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#C5E0E0" stopOpacity="0" />
                </linearGradient>
              </defs>
              
              {/* 영역 채우기 (지속시간 데이터) */}
              <path
                d="M 0 140 
                   L 21 135 L 42 145 L 63 130
                   L 84 125 L 105 140 L 126 138
                   L 147 120 L 168 115 L 189 110
                   L 210 105 L 231 100 L 252 95
                   L 273 90 L 294 85 L 315 80
                   L 336 75 L 357 70 L 378 65
                   L 399 60 L 420 58 L 441 55
                   L 462 60 L 483 70 L 504 75
                   L 525 80 L 546 85 L 567 90
                   L 588 95 L 609 100
                   L 609 160 L 0 160 Z"
                fill="url(#durationGradient)"
                className={styles.chartArea}
              />
              
              {/* 곡선 라인 */}
              <path
                d="M 0 140 
                   C 10.5 137.5, 31.5 138, 42 145
                   C 52.5 152, 73.5 127.5, 84 125
                   C 94.5 122.5, 115.5 139, 126 138
                   C 136.5 137, 157.5 117.5, 168 115
                   C 178.5 112.5, 199.5 107.5, 210 105
                   C 220.5 102.5, 241.5 97.5, 252 95
                   C 262.5 92.5, 283.5 87.5, 294 85
                   C 304.5 82.5, 325.5 77.5, 336 75
                   C 346.5 72.5, 367.5 67.5, 378 65
                   C 388.5 62.5, 409.5 59, 420 58
                   C 430.5 57, 451.5 57.5, 462 60
                   C 472.5 62.5, 493.5 72.5, 504 75
                   C 514.5 77.5, 535.5 82.5, 546 85
                   C 556.5 87.5, 577.5 92.5, 588 95
                   C 598.5 97.5, 609 100, 609 100"
                fill="none"
                stroke="#448181"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={styles.chartLine}
              />
              
              {/* 30개 데이터 포인트들 */}
              <circle cx="0" cy="140" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="21" cy="135" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="42" cy="145" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="63" cy="130" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="84" cy="125" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="105" cy="140" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="126" cy="138" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="147" cy="120" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="168" cy="115" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="189" cy="110" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="210" cy="105" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="231" cy="100" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="252" cy="95" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="273" cy="90" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="294" cy="85" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="315" cy="80" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="336" cy="75" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="357" cy="70" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="378" cy="65" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="399" cy="60" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="420" cy="58" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="441" cy="55" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="462" cy="60" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="483" cy="70" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="504" cy="75" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="525" cy="80" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="546" cy="85" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="567" cy="90" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="588" cy="95" r="2.5" fill="#448181" className={styles.dataPoint} />
              <circle cx="609" cy="100" r="2.5" fill="#448181" className={styles.dataPoint} />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardStatGraphs;